import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  List,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Check,
  Cpu,
  FileJson,
  Layers,
  MonitorSmartphone,
  Network,
  Radio,
  RefreshCw,
  Server,
  Shield,
  Zap,
} from 'lucide-react'
import settingsGif from '@/assets/gifs/settings.gif'
import mqttLogo from '@/assets/mqtt.png'
import sparkplugArchitecture from '@/assets/sparkplug-essentials-part-2-architecture-sparkplug-03-solution.webp'
import sparkplugLogo from '@/assets/sparkplugb.png'
import { PageHeader, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/education/sparkplug')({
  component: SparkplugEducationPage,
})

interface FeatureCardProps {
  icon: typeof Shield
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={5} mt="4" mb="4">
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface ConceptCardProps {
  image: string
  title: string
  children: React.ReactNode
  imageSize?: string
}

function ConceptCard({ image, title, children, imageSize = '120px' }: ConceptCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={6}>
        <VStack align="start" gap={4}>
          <Image src={image} alt={title} h={imageSize} objectFit="contain" />
          <VStack align="start" gap={2}>
            <Text fontWeight="bold" fontSize="lg">
              {title}
            </Text>
            {children}
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

const tags = ['Industrial IoT', 'SCADA', 'Interoperability', 'Eclipse Foundation']

function SparkplugEducationPage() {
  const navigate = useNavigate()

  const principles = [
    {
      icon: Radio,
      title: 'Publish / Subscribe',
      description:
        'Decoupled communication model enabling efficient data distribution across the network.',
    },
    {
      icon: Zap,
      title: 'Report by Exception',
      description:
        'Only send data when values change, reducing bandwidth and improving efficiency.',
    },
    {
      icon: RefreshCw,
      title: 'Continuous Session Awareness',
      description: 'Real-time awareness of device connection state across the entire system.',
    },
    {
      icon: Shield,
      title: 'Birth & Death Certificates',
      description: 'Automatic notifications when devices come online or go offline unexpectedly.',
    },
    {
      icon: Network,
      title: 'Persistent Connections',
      description:
        'Maintain session state through network interruptions for reliable communication.',
    },
    {
      icon: Layers,
      title: 'Auto Discovery',
      description: 'Automatically discover and register new devices without manual configuration.',
    },
    {
      icon: FileJson,
      title: 'Standardized Payload',
      description:
        'Consistent data format using Google Protocol Buffers for efficient serialization.',
    },
    {
      icon: Server,
      title: 'Topic Namespace',
      description: 'Well-defined topic structure for organizing data across groups and nodes.',
    },
  ]

  const components = [
    {
      icon: MonitorSmartphone,
      title: 'SCADA / IIoT Host',
      description: 'Primary application that subscribes to device data and sends commands.',
    },
    {
      icon: Network,
      title: 'Edge of Network (EoN) Nodes',
      description: 'Gateways that connect physical devices to the MQTT infrastructure.',
    },
    {
      icon: Cpu,
      title: 'Devices / Sensors',
      description: 'Physical devices and sensors that generate telemetry data.',
    },
    {
      icon: Layers,
      title: 'MQTT Application Nodes',
      description: 'Applications that process, analyze, or store the MQTT data.',
    },
    {
      icon: Server,
      title: 'MQTT Broker',
      description: 'Central hub that routes messages between all Sparkplug participants.',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Sparkplug B Specification"
        subtitle="The open-source specification that standardizes MQTT for Industrial IoT applications"
      />

      <VStack align="stretch" gap={10}>
        {/* Hero identity */}
        <VStack gap={6}>
          <Image src={sparkplugLogo} alt="Sparkplug" h="80px" objectFit="contain" />
          <HStack gap={2} flexWrap="wrap" justify="center">
            {tags.map((tag) => (
              <Box key={tag} px={3} py={1} bg="brand.solid" borderRadius="full">
                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                  {tag}
                </Text>
              </Box>
            ))}
          </HStack>
        </VStack>

        {/* Architecture Image */}
        <Card.Root overflow="hidden">
          <Card.Body p={6}>
            <Box display="flex" justifyContent="center">
              <Image
                src={sparkplugArchitecture}
                alt="Sparkplug Architecture"
                maxH="400px"
                objectFit="contain"
                borderRadius="lg"
              />
            </Box>
          </Card.Body>
        </Card.Root>

        {/* Introduction Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="What is Sparkplug?" />
          <Card.Root>
            <Card.Body p={6} mt="4" mb="4">
              <VStack align="start" gap={4}>
                <Text fontSize="md">
                  In the MQTT ecosystem,{' '}
                  <strong>Sparkplug is an open-source protocol specification</strong> designed to
                  standardize the communication for Industrial IoT (IIoT) applications by providing
                  a uniform way to structure data, enabling seamless interoperability.
                </Text>
                <Text fontSize="md">
                  While MQTT is widely used for message transport, it lacks standardized data
                  formats, leading to inconsistencies in data exchange across devices and systems.{' '}
                  <strong>
                    Sparkplug addresses this by defining payload structures and a stateful session
                    management system
                  </strong>
                  , which ensures consistent data handling, real-time updates, and device state
                  awareness.
                </Text>
                <Text fontSize="md">
                  This is especially crucial for industrial environments where maintaining the{' '}
                  <strong>integrity and accuracy of data in systems like SCADA and MES</strong> is
                  essential.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Requirements Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="MQTT Broker Requirements"
            description="Sparkplug requires a fully compliant MQTT 3.1.1 broker with specific features"
          />
          <SimpleGrid columns={{ base: 1, lg: 3 }} gap={6}>
            <ConceptCard image={mqttLogo} title="Requirements" imageSize="100px">
              <Text fontSize="sm" color="content.secondary" mb={2}>
                In order to use Sparkplug, you need an MQTT broker that implements 100% of the MQTT
                3.1.1 specification:
              </Text>
              <List.Root gap={1}>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">QoS 0 and 1 support</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Retained Messages</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Last Will and Testament</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Flexible security system</Text>
                  </HStack>
                </List.Item>
              </List.Root>
            </ConceptCard>

            <ConceptCard image={sparkplugLogo} title="Components" imageSize="100px">
              <Text fontSize="sm" color="content.secondary" mb={2}>
                A traditional Sparkplug architecture consists of the following components:
              </Text>
              <List.Root gap={1}>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">SCADA / IIoT Host</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Edge of Network (EoN) Nodes</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Devices / Sensors</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">MQTT Application Nodes</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">MQTT Broker</Text>
                  </HStack>
                </List.Item>
              </List.Root>
            </ConceptCard>

            <ConceptCard image={settingsGif} title="Principles" imageSize="80px">
              <Text fontSize="sm" color="content.secondary" mb={2}>
                The Sparkplug architecture builds upon these elegant principles:
              </Text>
              <List.Root gap={1}>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Pub / Sub & Report by Exception</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Continuous Session Awareness</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Death and Birth Certificates</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Auto discovery</Text>
                  </HStack>
                </List.Item>
                <List.Item>
                  <HStack gap={2}>
                    <Check size={14} color="var(--chakra-colors-green-600)" />
                    <Text fontSize="sm">Standardized payload & topics</Text>
                  </HStack>
                </List.Item>
              </List.Root>
            </ConceptCard>
          </SimpleGrid>
        </VStack>

        {/* Principles Section */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="What Makes Sparkplug Elegant"
            description="The principles that make Sparkplug superior to legacy industrial solutions"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
            {principles.map((principle) => (
              <FeatureCard key={principle.title} {...principle} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Architecture Components */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Sparkplug Components"
            description="The key participants in a Sparkplug-based IIoT architecture"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {components.map((component) => (
              <FeatureCard key={component.title} {...component} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Topic Namespace */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Sparkplug Topic Namespace"
            description="Well-defined topic structure for organizing industrial data"
          />
          <Card.Root>
            <Card.Body p={6} mt="4" mb="4">
              <VStack align="start" gap={4}>
                <Text fontWeight="bold" fontSize="lg">
                  Topic Format
                </Text>
                <Box
                  bg="gray.100"
                  _dark={{ bg: 'gray.700' }}
                  p={4}
                  borderRadius="lg"
                  w="100%"
                  fontFamily="mono"
                >
                  <Text fontSize="sm">spBv1.0/group_id/message_type/edge_node_id/[device_id]</Text>
                </Box>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="100%">
                  <VStack align="start" gap={2}>
                    <Text fontWeight="bold">Message Types:</Text>
                    <List.Root gap={1}>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>NBIRTH</strong> - Node birth certificate
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>NDEATH</strong> - Node death certificate
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>DBIRTH</strong> - Device birth certificate
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>DDEATH</strong> - Device death certificate
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>NDATA</strong> - Node data message
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>DDATA</strong> - Device data message
                        </Text>
                      </List.Item>
                    </List.Root>
                  </VStack>
                  <VStack align="start" gap={2}>
                    <Text fontWeight="bold">Command Types:</Text>
                    <List.Root gap={1}>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>NCMD</strong> - Node command
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>DCMD</strong> - Device command
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text fontSize="sm">
                          <strong>STATE</strong> - SCADA/Host state
                        </Text>
                      </List.Item>
                    </List.Root>
                    <Text fontWeight="bold" mt={2}>
                      Example:
                    </Text>
                    <Box
                      bg="gray.100"
                      _dark={{ bg: 'gray.700' }}
                      p={2}
                      borderRadius="md"
                      fontFamily="mono"
                    >
                      <Text fontSize="xs">spBv1.0/Factory1/DDATA/Line1/Sensor1</Text>
                    </Box>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Continue Learning</Heading>
                <Text color="content.secondary">
                  Explore HiveMQ DataHub for data quality and governance
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/datahub' })}>
                Explore DataHub
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
